/**
 * Data Quality API Client
 * Handles data validation, quality metrics, and reporting
 */

import { BaseApiClient } from './base-client';
import { ApiResponse } from './config';

// Data Quality Types
export interface DataQualityValidationRequest {
  symbol: string;
  timeframe: string;
  data: Array<{
    timestamp: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
  }>;
  source: string;
}

export interface DataQualityValidationResult {
  is_valid: boolean;
  quality_score: number; // 0-100
  issues: Array<{
    type: 'missing_data' | 'invalid_price' | 'volume_anomaly' | 'timestamp_gap' | 'price_spike';
    severity: 'low' | 'medium' | 'high' | 'critical';
    message: string;
    affected_records: number;
    suggestions: string[];
  }>;
  metrics: {
    completeness: number;
    accuracy: number;
    consistency: number;
    timeliness: number;
    validity: number;
  };
  summary: {
    total_records: number;
    valid_records: number;
    invalid_records: number;
    missing_records: number;
    duplicate_records: number;
  };
}

export interface DataQualityMetrics {
  source: string;
  symbol?: string;
  period: {
    start_date: string;
    end_date: string;
  };
  overall_score: number;
  metrics: {
    completeness: {
      score: number;
      missing_data_points: number;
      total_expected: number;
      percentage: number;
    };
    accuracy: {
      score: number;
      anomalies_detected: number;
      outliers_count: number;
      validation_errors: number;
    };
    consistency: {
      score: number;
      format_violations: number;
      schema_errors: number;
      duplicate_records: number;
    };
    timeliness: {
      score: number;
      delayed_updates: number;
      average_delay_minutes: number;
      max_delay_minutes: number;
    };
    validity: {
      score: number;
      invalid_values: number;
      constraint_violations: number;
      type_errors: number;
    };
  };
  trends: {
    score_trend: 'improving' | 'declining' | 'stable';
    trend_percentage: number;
    comparison_period: string;
  };
  last_updated: string;
}

export interface DataQualityReport {
  report_id: string;
  report_type: 'daily' | 'weekly' | 'monthly' | 'custom';
  period: {
    start_date: string;
    end_date: string;
  };
  sources: string[];
  symbols?: string[];
  summary: {
    overall_quality_score: number;
    total_issues: number;
    critical_issues: number;
    resolved_issues: number;
    data_sources_analyzed: number;
    symbols_analyzed: number;
  };
  source_analysis: Array<{
    source: string;
    quality_score: number;
    issues_count: number;
    uptime_percentage: number;
    average_latency_ms: number;
    error_rate: number;
  }>;
  issue_breakdown: Array<{
    type: string;
    count: number;
    severity_distribution: {
      low: number;
      medium: number;
      high: number;
      critical: number;
    };
  }>;
  recommendations: Array<{
    priority: 'low' | 'medium' | 'high' | 'critical';
    category: 'data_source' | 'validation' | 'monitoring' | 'infrastructure';
    title: string;
    description: string;
    estimated_impact: string;
  }>;
  generated_at: string;
}

export interface DataQualityRule {
  id: string;
  name: string;
  description: string;
  rule_type: 'completeness' | 'accuracy' | 'consistency' | 'timeliness' | 'validity';
  severity: 'low' | 'medium' | 'high' | 'critical';
  conditions: {
    field?: string;
    operator: 'equals' | 'not_equals' | 'greater_than' | 'less_than' | 'between' | 'null' | 'not_null';
    value?: any;
    threshold?: number;
  }[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface DataQualityAlert {
  id: string;
  rule_id: string;
  rule_name: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  source: string;
  symbol?: string;
  affected_records: number;
  triggered_at: string;
  acknowledged: boolean;
  acknowledged_by?: string;
  acknowledged_at?: string;
  resolved: boolean;
  resolved_at?: string;
  resolution_notes?: string;
}

export class DataQualityApiClient extends BaseApiClient {
  /**
   * Validate data quality
   */
  async validateData(data: DataQualityValidationRequest): Promise<ApiResponse<DataQualityValidationResult>> {
    return this.post('/data-quality/validate', data);
  }

  /**
   * Get data quality metrics for a source
   */
  async getQualityMetrics(
    source: string,
    symbol?: string,
    startDate?: string,
    endDate?: string
  ): Promise<ApiResponse<DataQualityMetrics>> {
    const params = new URLSearchParams();
    params.append('source', source);
    if (symbol) params.append('symbol', symbol);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    return this.get(`/data-quality/metrics?${params.toString()}`);
  }

  /**
   * Get quality metrics for multiple sources
   */
  async getMultiSourceMetrics(
    sources: string[],
    startDate?: string,
    endDate?: string
  ): Promise<ApiResponse<DataQualityMetrics[]>> {
    const params = new URLSearchParams();
    sources.forEach(source => params.append('sources', source));
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    return this.get(`/data-quality/metrics/multi-source?${params.toString()}`);
  }

  /**
   * Generate data quality report
   */
  async generateReport(request: {
    report_type: 'daily' | 'weekly' | 'monthly' | 'custom';
    start_date?: string;
    end_date?: string;
    sources?: string[];
    symbols?: string[];
    include_recommendations?: boolean;
  }): Promise<ApiResponse<{ report_id: string; status: string; estimated_completion: string }>> {
    return this.post('/data-quality/reports', request);
  }

  /**
   * Get data quality report
   */
  async getReport(reportId: string): Promise<ApiResponse<DataQualityReport>> {
    return this.get(`/data-quality/reports/${reportId}`);
  }

  /**
   * List data quality reports
   */
  async listReports(
    limit: number = 20,
    offset: number = 0,
    reportType?: string
  ): Promise<ApiResponse<{
    reports: Array<{
      report_id: string;
      report_type: string;
      period: { start_date: string; end_date: string };
      overall_quality_score: number;
      total_issues: number;
      generated_at: string;
    }>;
    total: number;
    has_more: boolean;
  }>> {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    if (reportType) params.append('report_type', reportType);

    return this.get(`/data-quality/reports?${params.toString()}`);
  }

  /**
   * Create data quality rule
   */
  async createRule(rule: Omit<DataQualityRule, 'id' | 'created_at' | 'updated_at'>): Promise<ApiResponse<DataQualityRule>> {
    return this.post('/data-quality/rules', rule);
  }

  /**
   * Update data quality rule
   */
  async updateRule(ruleId: string, updates: Partial<DataQualityRule>): Promise<ApiResponse<DataQualityRule>> {
    return this.put(`/data-quality/rules/${ruleId}`, updates);
  }

  /**
   * Delete data quality rule
   */
  async deleteRule(ruleId: string): Promise<ApiResponse<{ message: string }>> {
    return this.delete(`/data-quality/rules/${ruleId}`);
  }

  /**
   * Get data quality rules
   */
  async getRules(isActive?: boolean): Promise<ApiResponse<DataQualityRule[]>> {
    const endpoint = isActive !== undefined
      ? `/data-quality/rules?active=${isActive}`
      : '/data-quality/rules';
    return this.get(endpoint);
  }

  /**
   * Get data quality alerts
   */
  async getAlerts(
    acknowledged?: boolean,
    severity?: string,
    limit: number = 50
  ): Promise<ApiResponse<DataQualityAlert[]>> {
    const params = new URLSearchParams();
    if (acknowledged !== undefined) params.append('acknowledged', acknowledged.toString());
    if (severity) params.append('severity', severity);
    params.append('limit', limit.toString());

    const queryString = params.toString();
    return this.get(`/data-quality/alerts${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Acknowledge data quality alert
   */
  async acknowledgeAlert(alertId: string, notes?: string): Promise<ApiResponse<{ message: string }>> {
    return this.post(`/data-quality/alerts/${alertId}/acknowledge`, {
      notes: notes || ''
    });
  }

  /**
   * Resolve data quality alert
   */
  async resolveAlert(alertId: string, resolutionNotes: string): Promise<ApiResponse<{ message: string }>> {
    return this.post(`/data-quality/alerts/${alertId}/resolve`, {
      resolution_notes: resolutionNotes
    });
  }

  /**
   * Get data source health status
   */
  async getSourceHealth(): Promise<ApiResponse<Array<{
    source: string;
    status: 'healthy' | 'degraded' | 'unhealthy' | 'offline';
    uptime_percentage: number;
    last_successful_update: string;
    issues_count: number;
    quality_score: number;
    latency_ms: number;
  }>>> {
    return this.get('/data-quality/sources/health');
  }

  /**
   * Trigger manual data validation
   */
  async triggerValidation(
    source: string,
    symbol?: string,
    timeframe?: string
  ): Promise<ApiResponse<{
    validation_id: string;
    status: 'started' | 'queued';
    estimated_completion: string;
  }>> {
    return this.post('/data-quality/validate/trigger', {
      source,
      symbol,
      timeframe
    });
  }

  /**
   * Get validation status
   */
  async getValidationStatus(validationId: string): Promise<ApiResponse<{
    validation_id: string;
    status: 'pending' | 'running' | 'completed' | 'failed';
    progress_percentage: number;
    result?: DataQualityValidationResult;
    error_message?: string;
  }>> {
    return this.get(`/data-quality/validate/${validationId}/status`);
  }
}

